import {
	type IconRenderer,
	makeIcon,
} from "@memorang/ui/components/AppIcon/make-icon";

export type BottomTabRoute = {
	name: string;
	title: string;
	icon: { default: string; selected: string };
	enabled: boolean;
	custom?: boolean;
};

export type BottomTab = {
	name: string;
	title: string;
	enabled: boolean;
	icon: IconRenderer;
	activeIcon: IconRenderer;
};

export type BuildBottomTabsParams = {
	navigationRoutes: BottomTabRoute[];
	showStudyPackAsHome: boolean;
	practiceTestEnabled?: { web: boolean; mobile: boolean };
	practiceTestLabel?: string;
	isWebPlatform: boolean;
	isCmeApp: boolean;
	t: (key: string, options?: { defaultValue: string }) => string;
};

export const buildBottomTabs = ({
	navigationRoutes,
	showStudyPackAsHome,
	practiceTestEnabled,
	practiceTestLabel,
	isWebPlatform,
	isCmeApp,
	t,
}: BuildBottomTabsParams): BottomTab[] => {
	const practiceTestTabEnabled = isWebPlatform
		? !!practiceTestEnabled?.web
		: !!practiceTestEnabled?.mobile;

	const visibleRoutes = navigationRoutes.map((route) =>
		route.name === "cme" || route.name === "modules"
			? { ...route, enabled: route.enabled && isCmeApp }
			: route,
	);

	const toTab = (route: BottomTabRoute): BottomTab =>
		route.name === "home" && showStudyPackAsHome
			? {
					name: route.name,
					title: t("navbar.study-pack"),
					enabled: route.enabled,
					icon: makeIcon("study-pack-outline", true),
					activeIcon: makeIcon("study-pack", true),
				}
			: {
					name: route.name,
					title: t(`navbar.${route.name}`, { defaultValue: route.title }),
					enabled: route.enabled,
					icon: makeIcon(route.icon.default, route.custom),
					activeIcon: makeIcon(route.icon.selected, route.custom),
				};

	const showsMoreRoute = visibleRoutes.some(
		(route) => route.name === "more" && route.enabled,
	);

	const hasCmeRoute = visibleRoutes.some((route) => route.name === "cme");

	const hasModulesRoute = visibleRoutes.some(
		(route) => route.name === "modules",
	);

	return [
		...visibleRoutes.slice(0, 1).map(toTab),
		{
			name: "practice-tests",
			title: practiceTestLabel ?? t("dashboard.practiceTests.title"),
			enabled: practiceTestTabEnabled,
			icon: makeIcon("clipboard-check-outline"),
			activeIcon: makeIcon("clipboard-check"),
		},
		...visibleRoutes.slice(1).map(toTab),
		// cme.tsx and modules.tsx exist as route files, so expo-router auto-renders
		// their tabs unless a Tabs.Screen with href: null is declared for them.
		...(hasCmeRoute
			? []
			: [
					{
						name: "cme",
						title: t("navbar.cme"),
						enabled: isCmeApp,
						icon: makeIcon("school-outline"),
						activeIcon: makeIcon("school"),
					},
				]),
		...(hasModulesRoute
			? []
			: [
					{
						name: "modules",
						title: t("navbar.modules"),
						enabled: isCmeApp,
						icon: makeIcon("view-grid-outline"),
						activeIcon: makeIcon("view-grid"),
					},
				]),
		{
			name: "account/settings",
			title: t("navbar.accountTab"),
			enabled: !showsMoreRoute,
			icon: makeIcon("account-outline"),
			activeIcon: makeIcon("account"),
		},
	];
};
